import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, Font } from '@react-pdf/renderer';
import AddChild from "./AddChild";
import UpdatePersonalInfo from "./UpdatePersonalInfo";
import UpdateAddressInfo from "./UpdateAddressInfo";
import UpdateEducationInfo from "./UpdateEducationInfo";
import UpdateEmploymentInfo from "./UpdateEmploymentInfo";
import UpdateMaritalInfo from "./UpdateMaritalInfo";
import UpdateBaptismInfo from "./UpdateBaptismInfo";
import UpdateMarriageInfo from "./UpdateMarriageInfo";
import UpdateStatus from "./UpdateStatus";
import UpdateChurchInfo from "./UpdateChurchInfo";
import { FaArrowLeft, FaPlus, FaEdit, FaChild, FaUser, FaHome, FaGraduationCap, FaBriefcase, FaHeart, FaCross, FaRing, FaChurch, FaInfoCircle, FaFilePdf } from "react-icons/fa";
import { Badge, Button, Card, Collapse, Spin, Empty, Tabs, message, Drawer, Tooltip } from "antd";
import API_BASE_URL from "../config";
import dataEn from "../dataEn";
import dataKin from "../dataKin";
import { language } from "../tools/language";

const { Panel } = Collapse;
const { TabPane } = Tabs;
const data = language === "en" ? dataEn : dataKin;

// Register fonts
Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/opensans/v18/mem8YaGs126MiZpBA-UFVZ0b.woff2', fontWeight: 'normal' },
    { src: 'https://fonts.gstatic.com/s/opensans/v18/mem5YaGs126MiZpBA-UN7rgOUuhp.woff2', fontWeight: 'bold' }
  ]
});

// Professional minimal PDF styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
  },
  // Header styles
  header: {
    marginBottom: 25,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    borderBottomStyle: 'solid',
    textAlign: 'center',
  },
  churchName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e3a5f',
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  parishName: {
    fontSize: 12,
    fontWeight: '500',
    color: '#4b5563',
    marginBottom: 6,
  },
  reportTitle: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 4,
  },
  dateText: {
    fontSize: 8,
    color: '#9ca3af',
  },
  // Section styles
  section: {
    marginBottom: 16,
  },
  sectionHeader: {
    marginBottom: 10,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // Grid layout
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  gridItem: {
    width: '33.33%',
    paddingRight: 8,
    paddingBottom: 8,
  },
  label: {
    fontSize: 7,
    fontWeight: '500',
    color: '#6b7280',
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  value: {
    fontSize: 9,
    color: '#1f2937',
    fontWeight: 'normal',
  },
  // Badge styles
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 7,
    color: '#ffffff',
  },
  // Table styles
  table: {
    marginTop: 6,
    borderWidth: 0.5,
    borderColor: '#e5e7eb',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 6,
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
  },
  tableCell: {
    flex: 1,
    fontSize: 8,
    paddingHorizontal: 6,
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    paddingTop: 8,
    borderTopWidth: 0.5,
    borderTopColor: '#e5e7eb',
    textAlign: 'center',
  },
  footerText: {
    fontSize: 7,
    color: '#9ca3af',
  },
});

// PDF Document Component
const ChristianPDF = ({ person, children, formatDate }) => {
  // Helper functions for conditional rendering
  const shouldShowMaritalSection = () => {
    return person?.marital_status && person.marital_status !== 'Single';
  };

  const shouldShowSpouseSection = () => {
    return person?.marital_status === 'Married' && person?.spouse_name;
  };

  const shouldShowBaptismDetails = () => {
    return person?.baptized === 'Yego';
  };

  const shouldShowMarriageDetails = () => {
    return person?.church_wedding === 'Yego' || person?.civil_wedding === 'Yego';
  };

  const shouldShowEducationDetails = () => {
    return person?.education_level && person?.education_level !== 'N/A';
  };

  const shouldShowEmploymentDetails = () => {
    return person?.employed === 'Yego' && person?.job_description;
  };

  const shouldShowChurchDetails = () => {
    return person?.church_roles || person?.choir_member || person?.choir_group;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return '#10b981';
      case 'Inactive': return '#f59e0b';
      default: return '#ef4444';
    }
  };

  return (
    <Document>
      {/* First Page */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.churchName}>Eglise Methodiste Libre au Rwanda</Text>
          <Text style={styles.parishName}>Paroisse Kicukiro (EMLR Kicukiro)</Text>
          <Text style={styles.reportTitle}>Christian Profile Report</Text>
          <Text style={styles.dateText}>Generated on: {new Date().toLocaleDateString()}</Text>
        </View>

        {/* Personal Information */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Personal Information</Text>
          </View>
          <View style={styles.gridContainer}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Full Name</Text>
              <Text style={styles.value}>{person?.full_name || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>National ID</Text>
              <Text style={styles.value}>{person?.national_id || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Phone Number</Text>
              <Text style={styles.value}>{person?.phone_number || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>{person?.email || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Date of Birth</Text>
              <Text style={styles.value}>{formatDate(person?.date_of_birth)}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Gender</Text>
              <Text style={styles.value}>{person?.sex || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Status</Text>
              <View style={[styles.badge, { backgroundColor: getStatusColor(person?.status) }]}>
                <Text style={styles.badgeText}>{person?.status || 'Unknown'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Address Information */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Address Information</Text>
          </View>
          <View style={styles.gridContainer}>
            <View style={styles.gridItem}>
              <Text style={styles.label}>District</Text>
              <Text style={styles.value}>{person?.district || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Sector</Text>
              <Text style={styles.value}>{person?.sector || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Cell</Text>
              <Text style={styles.value}>{person?.cell || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Village</Text>
              <Text style={styles.value}>{person?.village || 'N/A'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.label}>Isibo</Text>
              <Text style={styles.value}>{person?.isibo || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* Education Section - Conditional */}
        {shouldShowEducationDetails() && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Education Information</Text>
            </View>
            <View style={styles.gridContainer}>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Education Level</Text>
                <Text style={styles.value}>{person?.education_level || 'N/A'}</Text>
              </View>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Field of Study</Text>
                <Text style={styles.value}>{person?.field_of_study || 'N/A'}</Text>
              </View>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Professional Training</Text>
                <Text style={styles.value}>{person?.professional_training || 'N/A'}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Employment Section - Conditional */}
        {shouldShowEmploymentDetails() && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Employment Information</Text>
            </View>
            <View style={styles.gridContainer}>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Job Description</Text>
                <Text style={styles.value}>{person?.job_description || 'N/A'}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Marital Section - Conditional */}
        {shouldShowMaritalSection() && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Marital Information</Text>
            </View>
            <View style={styles.gridContainer}>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Marital Status</Text>
                <Text style={styles.value}>{person?.marital_status || 'N/A'}</Text>
              </View>
              {shouldShowSpouseSection() && (
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Spouse Name</Text>
                  <Text style={styles.value}>{person?.spouse_name || 'N/A'}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Baptism Section - Conditional */}
        {shouldShowBaptismDetails() && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Baptism Information</Text>
            </View>
            <View style={styles.gridContainer}>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Baptism Date</Text>
                <Text style={styles.value}>{formatDate(person?.date_of_baptism)}</Text>
              </View>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Godparent Name</Text>
                <Text style={styles.value}>{person?.godparent_name || 'N/A'}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Marriage Section - Conditional */}
        {shouldShowMarriageDetails() && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Marriage Information</Text>
            </View>
            <View style={styles.gridContainer}>
              {person?.church_wedding === 'Yego' && (
                <>
                  <View style={styles.gridItem}>
                    <Text style={styles.label}>Church Wedding</Text>
                    <Text style={styles.value}>{person?.church_wedding}</Text>
                  </View>
                  <View style={styles.gridItem}>
                    <Text style={styles.label}>Church Wedding Date</Text>
                    <Text style={styles.value}>{formatDate(person?.church_wedding_date)}</Text>
                  </View>
                  <View style={styles.gridItem}>
                    <Text style={styles.label}>Church Wedding Parish</Text>
                    <Text style={styles.value}>{person?.church_wedding_parish || 'N/A'}</Text>
                  </View>
                </>
              )}
              {person?.civil_wedding === 'Yego' && (
                <>
                  <View style={styles.gridItem}>
                    <Text style={styles.label}>Civil Wedding</Text>
                    <Text style={styles.value}>{person?.civil_wedding}</Text>
                  </View>
                  <View style={styles.gridItem}>
                    <Text style={styles.label}>Civil Wedding Date</Text>
                    <Text style={styles.value}>{formatDate(person?.civil_wedding_date)}</Text>
                  </View>
                </>
              )}
            </View>
          </View>
        )}

        {/* Church Information Section - Conditional */}
        {shouldShowChurchDetails() && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Church Information</Text>
            </View>
            <View style={styles.gridContainer}>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Church Roles</Text>
                <Text style={styles.value}>{person?.church_roles || 'N/A'}</Text>
              </View>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Choir Member</Text>
                <Text style={styles.value}>{person?.choir_member || 'N/A'}</Text>
              </View>
              <View style={styles.gridItem}>
                <Text style={styles.label}>Choir Group</Text>
                <Text style={styles.value}>{person?.choir_group || 'N/A'}</Text>
              </View>
              <View style={styles.gridItem}>
                <Text style={styles.label}>School</Text>
                <Text style={styles.value}>{person?.school || 'N/A'}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            This is an official document from Eglise Methodiste Libre au Rwanda - Paroisse Kicukiro
          </Text>
          <Text style={[styles.footerText, { marginTop: 4 }]}>
            Generated on: {new Date().toLocaleString()}
          </Text>
        </View>
      </Page>

      {/* Second Page for Children Section if needed */}
      {children && children.length > 0 && (
        <Page size="A4" style={styles.page}>
          <View style={styles.header}>
            <Text style={styles.churchName}>Eglise Methodiste Libre au Rwanda</Text>
            <Text style={styles.parishName}>Paroisse Kicukiro (EMLR Kicukiro)</Text>
            <Text style={styles.reportTitle}>Children Information</Text>
          </View>

          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Children Information ({children.length})</Text>
            </View>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.tableCell, { fontWeight: 'bold', flex: 2 }]}>Full Name</Text>
                <Text style={[styles.tableCell, { fontWeight: 'bold', flex: 1.5 }]}>Date of Birth</Text>
                <Text style={[styles.tableCell, { fontWeight: 'bold', flex: 1 }]}>Status</Text>
              </View>
              {children.map((child, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{child.full_name}</Text>
                  <Text style={[styles.tableCell, { flex: 1.5 }]}>{formatDate(child.date_of_birth)}</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>{child.status}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.footer}>
            <Text style={styles.footerText}>
              This is an official document from Eglise Methodiste Libre au Rwanda - Paroisse Kicukiro
            </Text>
            <Text style={[styles.footerText, { marginTop: 4 }]}>
              Generated on: {new Date().toLocaleString()}
            </Text>
          </View>
        </Page>
      )}
    </Document>
  );
};

// Main Component
const ChristianDetails = ({ id, national_id, onBack }) => {
  const [person, setPerson] = useState(null);
  const [children, setChildren] = useState([]);
  const [showChildForm, setShowChildForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [isChildDrawerVisible, setIsChildDrawerVisible] = useState(false);
  const navigate = useNavigate();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'N/A';
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  };

  // Get user role from localStorage
  const userRole = localStorage.getItem("position")?.toLowerCase();

  const fetchChristianData = useCallback(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/christian/${id}`)
      .then((response) => {
        setPerson(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching person details:", error);
        setLoading(false);
        message.error("Failed to load member details");
      });
  }, [id]);

  const fetchChildrenData = useCallback(() => {
    axios.get(`${API_BASE_URL}/child/nid/${national_id}`)
      .then((response) => {
        setChildren(response.data);
      })
      .catch((error) => {
        console.error("Error fetching children details:", error);
      });
  }, [national_id]);

  useEffect(() => {
    fetchChristianData();
    fetchChildrenData();
  }, [fetchChristianData, fetchChildrenData]);

  const handleUpdateSuccess = () => {
    fetchChristianData();
    setEditingSection(null);
    message.success("Information updated successfully!");
  };

  const handleAdminUpdate = async () => {
    try {
      await axios.put(
        `${API_BASE_URL}/christian/update/${id}`,
        { status: 'Inactive' }
      );
      setPendingApproval(true);
      fetchChristianData();
      setEditingSection(null);
      message.info("Changes submitted for approval");
    } catch (error) {
      console.error("Error updating status to Inactive:", error);
      message.error("Failed to submit changes");
    }
  };

  const handleChildAdded = () => {
    fetchChildrenData();
    setShowChildForm(false);
    setIsChildDrawerVisible(false);
    message.success("Child added successfully!");
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const showUpdateButton = (sectionName) => {
    if (userRole === 'administrator') return true;
    if (sectionName === 'status' && userRole === 'admin') return false;
    if (userRole === 'staff') return false;
    return true;
  };

  // Conditional rendering logic
  const shouldShowMaritalSection = () => {
    return person?.marital_status && person.marital_status !== 'Single';
  };

  const shouldShowSpouseSection = () => {
    return person?.marital_status === 'Married' && person?.spouse_name;
  };

  const shouldShowBaptismDetails = () => {
    return person?.baptized === 'Yego';
  };

  const shouldShowMarriageDetails = () => {
    return person?.church_wedding === 'Yego' || person?.civil_wedding === 'Yego';
  };

  const shouldShowEmploymentDetails = () => {
    return person?.employed === 'Yego' && person?.job_description;
  };

  const shouldShowEducationDetails = () => {
    return person?.education_level && person?.education_level !== 'N/A';
  };

  const shouldShowChurchDetails = () => {
    return person?.church_roles || person?.choir_member || person?.choir_group;
  };

  const InfoItem = ({ label, value, icon }) => (
    <div className="p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <div className="flex items-start gap-3">
        {icon && <span className="text-[#164e63] mt-1">{icon}</span>}
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
          <p className="text-sm sm:text-base text-gray-900 mt-1">{value || "N/A"}</p>
        </div>
      </div>
    </div>
  );

  const SectionCard = ({ title, icon, sectionName, children }) => (
    <Card
      title={
        <div className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </div>
      }
      extra={
        !editingSection && showUpdateButton(sectionName) && (
          <Button
            size="small"
            icon={<FaEdit />}
            onClick={() => setEditingSection(sectionName)}
            className="text-[#164e63]"
          >
            Update
          </Button>
        )
      }
      className="mb-4 shadow-sm hover:shadow-md transition-shadow"
      bodyStyle={{ padding: isMobile ? '8px' : '16px' }}
    >
      {editingSection === sectionName ? (
        renderUpdateForm(sectionName)
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {children}
        </div>
      )}
    </Card>
  );

  const renderUpdateForm = (section) => {
    const commonProps = {
      christian: person,
      onUpdate: userRole === 'administrator' ? handleUpdateSuccess : handleAdminUpdate,
      onCancel: () => setEditingSection(null),
      isAdmin: userRole === 'admin',
      isAdministrator: userRole === 'administrator'
    };
  
    switch(section) {
      case 'personal':
        return <UpdatePersonalInfo {...commonProps} />;
      case 'address':
        return <UpdateAddressInfo {...commonProps} />;
      case 'education':
        return <UpdateEducationInfo {...commonProps} />;
      case 'employment':
        return <UpdateEmploymentInfo {...commonProps} />;
      case 'marital':
        return <UpdateMaritalInfo {...commonProps} />;
      case 'baptism':
        return <UpdateBaptismInfo {...commonProps} />;
      case 'marriage':
        return <UpdateMarriageInfo {...commonProps} />;
      case 'status':
        return <UpdateStatus {...commonProps} />;
      case 'church':
        return <UpdateChurchInfo {...commonProps} />;
      default:
        return null;
    }
  };

  const ChildrenSection = () => (
    <Card
      title={
        <div className="flex items-center gap-2">
          <FaChild className="text-[#164e63]" />
          <span>Children</span>
          <Badge count={children.length} className="ml-2" />
        </div>
      }
      extra={
        userRole !== 'staff' && (
          <Button
            size="small"
            icon={<FaPlus />}
            onClick={() => isMobile ? setIsChildDrawerVisible(true) : setShowChildForm(!showChildForm)}
            className="bg-[#164e63] text-white"
          >
            Add Child
          </Button>
        )
      }
      className="mb-4 shadow-sm"
    >
      {showChildForm && !isMobile && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <AddChild 
            national_id={national_id} 
            onChildAdded={handleChildAdded} 
            isAdmin={userRole === 'admin'}
          />
        </div>
      )}

      {children.length === 0 ? (
        <Empty 
          description="No children found" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className="py-8"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date of Birth</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {children.map((child) => (
                <tr key={child.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{child.full_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(child.date_of_birth)}</td>
                  <td className="px-4 py-3">
                    <Badge 
                      color={child.status === 'Yarakiriwe' ? 'green' : 'red'}
                      text={child.status}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  const ChildDrawer = () => (
    <Drawer
      title="Add Child"
      placement="bottom"
      onClose={() => setIsChildDrawerVisible(false)}
      open={isChildDrawerVisible}
      height="80%"
    >
      <AddChild 
        national_id={national_id} 
        onChildAdded={handleChildAdded} 
        isAdmin={userRole === 'admin'}
      />
    </Drawer>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" tip="Loading member details..." />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="text-center py-10">
        <Empty description="Member not found" />
        <Button onClick={handleBack} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-4 sm:py-6 px-3 sm:px-5">
      {pendingApproval && (
        <div className="mb-4 p-4 bg-yellow-100 text-yellow-800 rounded-lg flex items-start gap-3">
          <FaInfoCircle className="mt-0.5" />
          <div>
            <p className="font-semibold">Pending Approval</p>
            <p className="text-sm">Your changes have been submitted and require administrator approval. The member's status has been set to "Inactive" until approved.</p>
          </div>
        </div>
      )}
      
      <div className="max-w-6xl mx-auto">
        {/* Header with Export Button */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <Button 
            icon={<FaArrowLeft />} 
            onClick={handleBack}
            className="hover:bg-gray-100"
          >
            Back
          </Button>
          <div className="text-center sm:text-left">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{person.full_name}</h1>
            <Badge 
              className="mt-1"
              color={
                person.status === 'Active' ? 'green' : 
                person.status === 'Inactive' ? 'orange' : 'red'
              }
              text={person.status}
            />
          </div>
          <div className="flex gap-2">
            <PDFDownloadLink
              document={<ChristianPDF person={person} children={children} formatDate={formatDate} />}
              fileName={`${person.full_name.replace(/\s/g, '_')}_profile.pdf`}
            >
              {({ loading: pdfLoading }) => (
                <Button 
                  icon={<FaFilePdf />} 
                  loading={pdfLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Export PDF
                </Button>
              )}
            </PDFDownloadLink>
          </div>
        </div>

        {/* Mobile Tabs View */}
        {isMobile ? (
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            className="bg-white rounded-lg shadow-sm"
            tabBarStyle={{ padding: '0 8px' }}
          >
            <TabPane tab={<span className="flex items-center gap-1"><FaUser size={12} /> Personal</span>} key="personal">
              <div className="p-3">
                <InfoItem label="Email" value={person.email} icon={<FaUser />} />
                <InfoItem label="Phone Number" value={person.phone_number} icon={<FaUser />} />
                <InfoItem label="National ID" value={person.national_id} icon={<FaUser />} />
                <InfoItem label="Date of Birth" value={formatDate(person.date_of_birth)} icon={<FaUser />} />
                <InfoItem label="Gender" value={person.sex} icon={<FaUser />} />
                {showUpdateButton('personal') && (
                  <Button 
                    size="small"
                    icon={<FaEdit />}
                    onClick={() => setEditingSection('personal')}
                    className="w-full mt-4 bg-[#164e63] text-white"
                  >
                    Edit Personal Info
                  </Button>
                )}
              </div>
            </TabPane>
            
            <TabPane tab={<span className="flex items-center gap-1"><FaHome size={12} /> Address</span>} key="address">
              <div className="p-3">
                <InfoItem label="District" value={person.district} icon={<FaHome />} />
                <InfoItem label="Sector" value={person.sector} icon={<FaHome />} />
                <InfoItem label="Cell" value={person.cell} icon={<FaHome />} />
                <InfoItem label="Village" value={person.village} icon={<FaHome />} />
                <InfoItem label="Isibo" value={person.isibo} icon={<FaHome />} />
                {showUpdateButton('address') && (
                  <Button 
                    size="small"
                    icon={<FaEdit />}
                    onClick={() => setEditingSection('address')}
                    className="w-full mt-4 bg-[#164e63] text-white"
                  >
                    Edit Address
                  </Button>
                )}
              </div>
            </TabPane>
            
            {shouldShowEducationDetails() && (
              <TabPane tab={<span className="flex items-center gap-1"><FaGraduationCap size={12} /> Education</span>} key="education">
                <div className="p-3">
                  <InfoItem label="Education Level" value={person.education_level} icon={<FaGraduationCap />} />
                  <InfoItem label="Field of Study" value={person.field_of_study} icon={<FaGraduationCap />} />
                  <InfoItem label="Professional Training" value={person.professional_training} icon={<FaGraduationCap />} />
                  {showUpdateButton('education') && (
                    <Button 
                      size="small"
                      icon={<FaEdit />}
                      onClick={() => setEditingSection('education')}
                      className="w-full mt-4 bg-[#164e63] text-white"
                    >
                      Edit Education
                    </Button>
                  )}
                </div>
              </TabPane>
            )}
            
            {shouldShowEmploymentDetails() && (
              <TabPane tab={<span className="flex items-center gap-1"><FaBriefcase size={12} /> Employment</span>} key="employment">
                <div className="p-3">
                  <InfoItem label="Employed" value={person.employed} icon={<FaBriefcase />} />
                  <InfoItem label="Job Description" value={person.job_description} icon={<FaBriefcase />} />
                  {showUpdateButton('employment') && (
                    <Button 
                      size="small"
                      icon={<FaEdit />}
                      onClick={() => setEditingSection('employment')}
                      className="w-full mt-4 bg-[#164e63] text-white"
                    >
                      Edit Employment
                    </Button>
                  )}
                </div>
              </TabPane>
            )}
            
            {shouldShowMaritalSection() && (
              <TabPane tab={<span className="flex items-center gap-1"><FaHeart size={12} /> Marital</span>} key="marital">
                <div className="p-3">
                  <InfoItem label="Marital Status" value={person.marital_status} icon={<FaHeart />} />
                  {shouldShowSpouseSection() && (
                    <InfoItem label="Spouse Name" value={person.spouse_name} icon={<FaHeart />} />
                  )}
                  {showUpdateButton('marital') && (
                    <Button 
                      size="small"
                      icon={<FaEdit />}
                      onClick={() => setEditingSection('marital')}
                      className="w-full mt-4 bg-[#164e63] text-white"
                    >
                      Edit Marital Info
                    </Button>
                  )}
                </div>
              </TabPane>
            )}
            
            {shouldShowBaptismDetails() && (
              <TabPane tab={<span className="flex items-center gap-1"><FaCross size={12} /> Baptism</span>} key="baptism">
                <div className="p-3">
                  <InfoItem label="Baptized" value={person.baptized} icon={<FaCross />} />
                  <InfoItem label="Baptism Date" value={formatDate(person.date_of_baptism)} icon={<FaCross />} />
                  <InfoItem label="Godparent" value={person.godparent_name} icon={<FaCross />} />
                  {showUpdateButton('baptism') && (
                    <Button 
                      size="small"
                      icon={<FaEdit />}
                      onClick={() => setEditingSection('baptism')}
                      className="w-full mt-4 bg-[#164e63] text-white"
                    >
                      Edit Baptism
                    </Button>
                  )}
                </div>
              </TabPane>
            )}
            
            {shouldShowMarriageDetails() && (
              <TabPane tab={<span className="flex items-center gap-1"><FaRing size={12} /> Marriage</span>} key="marriage">
                <div className="p-3">
                  {person?.church_wedding === 'Yego' && (
                    <>
                      <InfoItem label="Church Wedding" value={person.church_wedding} icon={<FaRing />} />
                      <InfoItem label="Church Wedding Date" value={formatDate(person.church_wedding_date)} icon={<FaRing />} />
                      <InfoItem label="Church Wedding Parish" value={person.church_wedding_parish} icon={<FaRing />} />
                    </>
                  )}
                  {person?.civil_wedding === 'Yego' && (
                    <>
                      <InfoItem label="Civil Wedding" value={person.civil_wedding} icon={<FaRing />} />
                      <InfoItem label="Civil Wedding Date" value={formatDate(person.civil_wedding_date)} icon={<FaRing />} />
                    </>
                  )}
                  {showUpdateButton('marriage') && (
                    <Button 
                      size="small"
                      icon={<FaEdit />}
                      onClick={() => setEditingSection('marriage')}
                      className="w-full mt-4 bg-[#164e63] text-white"
                    >
                      Edit Marriage
                    </Button>
                  )}
                </div>
              </TabPane>
            )}
            
            {shouldShowChurchDetails() && (
              <TabPane tab={<span className="flex items-center gap-1"><FaChurch size={12} /> Church</span>} key="church">
                <div className="p-3">
                  <InfoItem label="Church Roles" value={person.church_roles} icon={<FaChurch />} />
                  <InfoItem label="Choir Member" value={person.choir_member} icon={<FaChurch />} />
                  <InfoItem label="Choir Group" value={person.choir_group} icon={<FaChurch />} />
                  <InfoItem label="School" value={person.school} icon={<FaChurch />} />
                  {showUpdateButton('church') && (
                    <Button 
                      size="small"
                      icon={<FaEdit />}
                      onClick={() => setEditingSection('church')}
                      className="w-full mt-4 bg-[#164e63] text-white"
                    >
                      Edit Church Info
                    </Button>
                  )}
                </div>
              </TabPane>
            )}
            
            <TabPane tab={<span className="flex items-center gap-1"><FaChild size={12} /> Children ({children.length})</span>} key="children">
              <div className="p-3">
                <ChildrenSection />
              </div>
            </TabPane>
          </Tabs>
        ) : (
          // Desktop Accordion View with SectionCards
          <Collapse defaultActiveKey={['personal']} className="bg-white rounded-lg shadow-sm">
            <Panel header={<span className="flex items-center gap-2"><FaUser /> Personal Information</span>} key="personal">
              <SectionCard title="Personal Information" icon={<FaUser />} sectionName="personal">
                <InfoItem label="Email" value={person.email} />
                <InfoItem label="Phone Number" value={person.phone_number} />
                <InfoItem label="National ID" value={person.national_id} />
                <InfoItem label="Date of Birth" value={formatDate(person.date_of_birth)} />
                <InfoItem label="Gender" value={person.sex} />
              </SectionCard>
            </Panel>
            
            <Panel header={<span className="flex items-center gap-2"><FaHome /> Address</span>} key="address">
              <SectionCard title="Address" icon={<FaHome />} sectionName="address">
                <InfoItem label="District" value={person.district} />
                <InfoItem label="Sector" value={person.sector} />
                <InfoItem label="Cell" value={person.cell} />
                <InfoItem label="Village" value={person.village} />
                <InfoItem label="Isibo" value={person.isibo} />
              </SectionCard>
            </Panel>
            
            {shouldShowEducationDetails() && (
              <Panel header={<span className="flex items-center gap-2"><FaGraduationCap /> Education</span>} key="education">
                <SectionCard title="Education" icon={<FaGraduationCap />} sectionName="education">
                  <InfoItem label="Education Level" value={person.education_level} />
                  <InfoItem label="Field of Study" value={person.field_of_study} />
                  <InfoItem label="Professional Training" value={person.professional_training} />
                </SectionCard>
              </Panel>
            )}
            
            {shouldShowEmploymentDetails() && (
              <Panel header={<span className="flex items-center gap-2"><FaBriefcase /> Employment</span>} key="employment">
                <SectionCard title="Employment" icon={<FaBriefcase />} sectionName="employment">
                  <InfoItem label="Employed" value={person.employed} />
                  <InfoItem label="Job Description" value={person.job_description} />
                </SectionCard>
              </Panel>
            )}
            
            {shouldShowMaritalSection() && (
              <Panel header={<span className="flex items-center gap-2"><FaHeart /> Marital Status</span>} key="marital">
                <SectionCard title="Marital Status" icon={<FaHeart />} sectionName="marital">
                  <InfoItem label="Marital Status" value={person.marital_status} />
                  {shouldShowSpouseSection() && (
                    <InfoItem label="Spouse Name" value={person.spouse_name} />
                  )}
                </SectionCard>
              </Panel>
            )}
            
            {shouldShowBaptismDetails() && (
              <Panel header={<span className="flex items-center gap-2"><FaCross /> Baptism</span>} key="baptism">
                <SectionCard title="Baptism" icon={<FaCross />} sectionName="baptism">
                  <InfoItem label="Baptized" value={person.baptized} />
                  <InfoItem label="Baptism Date" value={formatDate(person.date_of_baptism)} />
                  <InfoItem label="Godparent Name" value={person.godparent_name} />
                </SectionCard>
              </Panel>
            )}
            
            {shouldShowMarriageDetails() && (
              <Panel header={<span className="flex items-center gap-2"><FaRing /> Marriage</span>} key="marriage">
                <SectionCard title="Marriage" icon={<FaRing />} sectionName="marriage">
                  {person?.church_wedding === 'Yego' && (
                    <>
                      <InfoItem label="Church Wedding" value={person.church_wedding} />
                      <InfoItem label="Church Wedding Date" value={formatDate(person.church_wedding_date)} />
                      <InfoItem label="Church Wedding Parish" value={person.church_wedding_parish} />
                    </>
                  )}
                  {person?.civil_wedding === 'Yego' && (
                    <>
                      <InfoItem label="Civil Wedding" value={person.civil_wedding} />
                      <InfoItem label="Civil Wedding Date" value={formatDate(person.civil_wedding_date)} />
                    </>
                  )}
                </SectionCard>
              </Panel>
            )}
            
            {shouldShowChurchDetails() && (
              <Panel header={<span className="flex items-center gap-2"><FaChurch /> Church Information</span>} key="church">
                <SectionCard title="Church Information" icon={<FaChurch />} sectionName="church">
                  <InfoItem label="Church Roles" value={person.church_roles} />
                  <InfoItem label="Choir Member" value={person.choir_member} />
                  <InfoItem label="Choir Group" value={person.choir_group} />
                  <InfoItem label="School" value={person.school} />
                </SectionCard>
              </Panel>
            )}
            
            <Panel header={<span className="flex items-center gap-2"><FaChild /> Children ({children.length})</span>} key="children">
              <ChildrenSection />
            </Panel>
          </Collapse>
        )}
      </div>
      
      <ChildDrawer />
    </div>
  );
};

export default ChristianDetails;